import { Column, ColumnType } from '../types';
import moment from 'moment';
import { TFunction } from 'react-i18next';

export const today = moment().format('YYYY-MM-DD HH:mm:ss');

// format to used to determine us format if year is last and to determine if is US format date
const yearLastDateRegexp = /^(?<monthOrDay01>\d{2})[/-](?<monthOrDay02>\d{2})[/-]\d{4}/;

const matchesDesiredOutputRegex = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/;

// format starting YYYY-MM-DD or YYYY/MM/DD or YYYYMMDD with time consideration
const yearFirstDateTimeRegexp = /^(?<year>\d{4})[/-]?(?<month>\d{2})[/-]?(?<day>\d{2})([T\s](?<hour>\d{1,2}):(?<min>\d{1,2}):?(?<sec>\d{1,2})?)?/;
// format starting MM-DD-YYYY or MM/DD/YYYY or with time consideration
const usDateTimeRegexp = /^(?<month>\d{2})[/-](?<day>\d{2})[/-](?<year>\d{4})([T\s](?<hour>\d{1,2}):(?<min>\d{1,2}):?(?<sec>\d{1,2})?)?/;
// format starting DD-MM-YYYY or DD/MM/YYYY with time consideration
const nonUsDateTimeRegexp = /^(?<day>\d{2})[/-](?<month>\d{2})[/-](?<year>\d{4})([T\s](?<hour>\d{1,2}):(?<min>\d{1,2}):?(?<sec>\d{1,2})?)?/;
// format starting YYYY-MM-DD or YYYY/MM/DD or YYYYMMDD
const yearFirstDateRegexp = /^\d{4}[/-]?\d{2}[/-]?\d{2}/;

function convertDates(dates: string[]): string[] {
  const processDates = (dates: string[], format: RegExp): string[] => {
    return dates.map(date => {
      const match = format.exec(date);
      if (match) {
        let hour = parseInt(match.groups.hour ?? '0');
        if (date.includes('PM')) {
          hour += 12;
        }

        const datePart = `${match.groups.year}-${match.groups.month}-${match.groups.day}`;
        const timePart = ` ${hour.toString().padStart(2, '0')}:${(match.groups.min ?? '').padStart(2, '0')}:${(
          match.groups.sec ?? ''
        ).padStart(2, '0')}`;
        return datePart + timePart;
      }
      return null;
    });
  };

  const isUSDateTimeFormat = (dates: string[]): boolean => {
    let isUsDateFormat;
    for (const date of dates) {
      const match = yearLastDateRegexp.exec(date);

      if (match) {
        const part1 = parseInt(match.groups.monthOrDay01);
        const part2 = parseInt(match.groups.monthOrDay02);

        if (part1 >= 13) {
          isUsDateFormat = false;
          break;
        }

        if (part2 >= 13) {
          isUsDateFormat = true;
          break;
        }
      }
    }

    return isUsDateFormat ?? false;
  };

  if (matchesDesiredOutputRegex.test(dates[0])) {
    return dates;
  }

  if (yearFirstDateRegexp.test(dates[0])) {
    return processDates(dates, yearFirstDateTimeRegexp);
  }

  if (yearLastDateRegexp.test(dates[0])) {
    if (isUSDateTimeFormat(dates)) {
      return processDates(dates, usDateTimeRegexp);
    } else {
      // this means at least one of the first part is beyond 12, so we fallback to thinking it as day
      // hence, falling back to Non-US DD[sep]MM[sep]... format
      return processDates(dates, nonUsDateTimeRegexp);
    }
  }

  return [];
}

export function buildConsentCsv(
  emailColumn: Column,
  dateColumn: Column,
  translator: TFunction<'fileManagement'>,
): string {
  if (emailColumn.type !== ColumnType.EMAIL) {
    throw new TypeError(translator('upload.error.emailDataIncorrect'));
  }

  if (dateColumn.type !== ColumnType.DATE) {
    throw new TypeError(translator('upload.error.dateDataIncorrect'));
  }

  let result = 'EmailAddress,EffectiveDate';
  const cleanDates = dateColumn.rows.length === 0 ? [] : convertDates(dateColumn.rows);

  emailColumn.rows.forEach((email: string, index: number) => {
    result += `\r\n${email},${cleanDates[index] ?? today}`;
  });

  return result;
}
